/**
 * Where a sheet grows from (docs/UI_V4_SHEETS.md). A detail sheet opens out of the control that was tapped and closes back
 * into it; this module is the one place that knows which control that was. It ships in the first load (it is ~1 KB) because
 * the opener runs before the sheets chunk exists.
 *
 * Nothing has to be wired for it to work: one capture-phase `click` listener on the document remembers the control that was
 * activated (a mouse click, a tap, Enter or Space on a focused button all end in a `click`). The nearest ancestor that is a
 * `[data-grow]`, else a `[data-m]` button, a ladder row, an instrument, a `button`, an `a` or a `role=button`, is the source.
 * A zone that wants a different element to be the source (a hero line whose button is only part of what should grow) puts
 * `data-grow` on it, or calls `setSheetOrigin(el)` right before it opens the sheet.
 *
 * The record is short-lived on purpose (a click that did not open anything must never steer a sheet that opens a second
 * later from the keyboard): the sheet reads it when it mounts, within `ORIGIN_FRESH_MS` of the click.
 */

export interface SheetOrigin {
  el: HTMLElement
  /** The control's box at the moment of the tap (the live box is preferred while the element is still attached). */
  rect: DOMRect
  at: number
}

/** A tap older than this opened nothing (or was slow to: a cold first open still waits for the chunk). */
export const ORIGIN_FRESH_MS = 1200

/** What counts as "the thing that was tapped", most specific first. */
export const ORIGIN_SELECTOR = '[data-grow], [data-m], .ladder-row, .instr, button, a[href], [role="button"]'

/** Controls that live inside a dialog never start a grow (the metric switcher, the day strip, Earlier…). */
const INSIDE_OVERLAY = '.sheet-root, .glass-overlay, [aria-modal="true"]'

let last: SheetOrigin | null = null
let installed = false

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

function visible(rect: DOMRect): boolean {
  return rect.width > 4 && rect.height > 4
}

/** Remember `el` as the source of the sheet that is about to open (an explicit opener; clicks do it automatically). */
export function setSheetOrigin(el: Element | null): void {
  if (!el || !(el as HTMLElement).getBoundingClientRect) {
    last = null
    return
  }
  const rect = (el as HTMLElement).getBoundingClientRect()
  last = visible(rect) ? { el: el as HTMLElement, rect, at: now() } : null
}

/** The fresh source, or null. Does not consume (a double-invoked initializer must see the same answer): `clearSheetOrigin`. */
export function peekSheetOrigin(): SheetOrigin | null {
  if (!last) return null
  if (now() - last.at > ORIGIN_FRESH_MS) {
    last = null
    return null
  }
  return last
}

export function clearSheetOrigin(): void {
  last = null
}

function onClick(e: Event): void {
  const target = e.target as Element | null
  if (!target?.closest) return
  if (target.closest(INSIDE_OVERLAY)) return
  const el = target.closest<HTMLElement>(ORIGIN_SELECTOR)
  if (!el) {
    last = null
    return
  }
  setSheetOrigin(el)
}

/** Idempotent. Called by SheetHost (first load). Returns the cleanup (tests). */
export function installSheetOrigin(): () => void {
  if (installed || typeof document === 'undefined') return () => undefined
  if (document.documentElement.hasAttribute('data-v5')) return () => undefined
  installed = true
  document.addEventListener('click', onClick, { capture: true, passive: true })
  return () => {
    document.removeEventListener('click', onClick, { capture: true })
    installed = false
    last = null
  }
}
